import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import AnalyticsHelper from '../helper/AnalyticsHelper';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api';
import {
  isDemoCookieSession,
  isDemoOpenedInThisTab,
  clearDemoSession,
} from 'shared/helpers/messelloDemo';

const ONBOARDING_STEPS = ['account_details', 'enrichment'];
const routes = [...dashboard.routes];

export const router = createRouter({ history: createWebHistory(), routes });

export const validateAuthenticateRoutePermission = async (to, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    window.location.assign('/app/login');
    return '';
  }

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  const routeAccountId = Number(to.params?.accountId || accountId);
  const userAccount = accounts.find(a => a.id === routeAccountId);
  const isAdmin = userAccount?.role === 'administrator';
  const isActive = userAccount?.status === 'active';
  const needsOnboarding =
    ONBOARDING_STEPS.includes(userAccount?.onboarding_step) &&
    isAdmin &&
    isActive;

  if (to.name === 'no_accounts' || !to.name) {
    const target = needsOnboarding ? 'onboarding' : 'dashboard';
    return next(frontendURL(`accounts/${routeAccountId}/${target}`));
  }

  if (needsOnboarding && !isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/onboarding`));
  }
  if (!needsOnboarding && isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/dashboard`));
  }

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

// Messello: keep the public demo session ephemeral. If the demo account is
// authenticated but this tab is not the one that opened it (a reopened tab, a
// new tab, or a restored session after the browser closed), drop the session
// instead of silently landing the visitor back in the demo dashboard. Returns
// true when it has taken over navigation (a full-page redirect is in flight).
const enforceDemoEphemerality = () => {
  const user = store.getters.getCurrentUser || {};
  const accounts = user.accounts || [];
  const isDemoAccount = accounts.some(account => account.is_demo);

  if (isDemoAccount && !isDemoOpenedInThisTab()) {
    // clearCookiesOnLogout clears the auth cookie + storage and redirects to
    // the landing, so the visitor must click the demo link again to re-enter.
    clearDemoSession();
    clearCookiesOnLogout();
    return true;
  }

  // Real (non-demo) login now owns the cookie — drop any stale demo flag.
  if (!isDemoAccount && isDemoCookieSession()) {
    clearDemoSession();
  }

  return false;
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');

  router.beforeEach(async (to, _from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    await userAuthentication;
    if (enforceDemoEphemerality()) return;
    await validateAuthenticateRoutePermission(to, next, store);
  });
};

export default router;
